import React, { useRef } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import useFlow from 'modules/flow/state/use-flow'
import useOrder from '../../../order/state/order/use-order'
import useConfig from 'modules/config/state/use-config'
import { useTranslation } from 'next-i18next'
import FlowTwoColumnStepLayout from '../two-column-step-layout/two-column-step-layout'
import OrderCustomQuestionField from 'modules/order/components/custom-questions/custom-question-field'
import { OrderCustomAnswersSlice } from 'modules/order/state/custom-answers/custom-answers-types'
import { Outer } from '../two-column-step-layout/two-column-step-layout.styled'
import {
  trackFormSubmitEvent,
  trackFormErrorEvent,
} from '../../../analytics/helpers/track-form-events'

import useTrackUIElementEvent from '../../../analytics/hooks/useTrackUIElementEvent'
import mParticleSdk from '@mparticle/web-sdk'

type FormData = OrderCustomAnswersSlice

const RequiredCustomQuestionStep = () => {
  const flow = useFlow()
  const order = useOrder()
  const config = useConfig()
  const { t } = useTranslation()

  const trackUIElementEvent = useTrackUIElementEvent()

  const questionId = Number(flow.currentStep.name.split('-')[1])
  const questionSchema = order.customAnswers.questionSchema(questionId)
  const questionConfig = config.questions.custom.find((question) => question.id === questionId)

  const form = useForm<FormData>({
    values: { ...order.customAnswers.value },
    resolver: questionSchema ? yupResolver(questionSchema) : undefined,
  })

  // Track the last error to avoid sending multiple of the same error
  const lastTrackedErrorRef = useRef<string | null>(null)

  const hasError = !!Object.keys(form.formState.errors).length

  const goNext = () => {
    if (hasError) {
      // Track form error if it's different from the last tracked error
      const currentError = JSON.stringify(form.formState.errors)

      if (lastTrackedErrorRef.current !== currentError) {
        trackFormErrorEvent(flow.currentStep.name, currentError)

        lastTrackedErrorRef.current = currentError
      }

      return
    }

    const nextStep = flow.nextStep

    if (nextStep) {
      flow.goToStep(nextStep.name)
    }
  }

  const onSubmit = (data: FormData) => {
    // Track form submit event when the form is successfully submitted
    trackFormSubmitEvent(flow.currentStep.name)

    trackUIElementEvent('Button Clicked', mParticleSdk.EventType.Other, {
      tracking_id: 'custom question submitted',
      text: t('RequiredCustomQuestionStep.footer.continueButton', 'Continue'),
      link_url: window.location.href,
      outbound: false,
    })

    order.customAnswers.setValue(data as Record<string, string>)
    goNext()
  }

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} style={{ display: 'contents' }}>
        <Outer>
          <FlowTwoColumnStepLayout.Header
            title={flow.currentStep.title}
            backButtonDataId="custom-question-pre-conversion-step-back-button"
          />
          <FlowTwoColumnStepLayout.CenteredBody>
            {questionConfig && <OrderCustomQuestionField question={questionConfig} />}
          </FlowTwoColumnStepLayout.CenteredBody>
          <FlowTwoColumnStepLayout.Footer>
            <FlowTwoColumnStepLayout.FooterButton
              size="lg"
              type="submit"
              disabled={!form.formState.isValid}
              dataId="custom-question-pre-conversion-step-continue-button"
            >
              {t('RequiredCustomQuestionStep.footer.continueButton', 'Continue')}
            </FlowTwoColumnStepLayout.FooterButton>
          </FlowTwoColumnStepLayout.Footer>
        </Outer>
      </form>
    </FormProvider>
  )
}

export default RequiredCustomQuestionStep
